/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState, useRef, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    Box,
    Center,
    Flex,
    Spinner,
    Stack,

    // Hooks
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import {ListMenuPopover} from './partial/list-menu-popover'
import Link from '@salesforce/retail-react-app/app/components/link'

/* Context */
import {useGlobalNavigation, useGlobalHeader} from '../../contexts/global-elements-context'
import {useHeaderContext} from '../../contexts/header-context'

/* Utils */
import {
    mergeCMSCategoryReplacements,
    applyCategoryAddReplace,
    getMegaMenuLinkCtaData
} from '../../components/optimizely/utils/utils'
import {renderComponentsSwitch} from '../../components/optimizely/utils/contentRenderer'
import {isGDILinkUsable, GDI_LINK_TYPES} from '../optimizely/gdi/utils/gdi-link-resolver'
import {getMegaMenuLinkSx} from '../optimizely/gdi/utils/gdi-cta-style'
import {useGetLinkData} from '../../contexts/category-url-context'
import GDILinkBlock from '../optimizely/gdi/assets/gdiLinkBlock'

// Others
import {categoryUrlBuilder} from '../../utils/url'

// Constants
const MAXIMUM_NUMBER_COLUMNS = 6
const OPEN_DELAY = 300
const CLOSE_DELAY = 300
const CLOSE_ANIMATION_MS = 200

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 *
 * @param maxColumns The maximum number of columns that we want to use per row inside the ListMenu.
 * @param root
 */
const ListMenu = ({
    root,
    contentComponent,
    categories,
    itemsKey,
    itemsCountKey,
    maxColumns = MAXIMUM_NUMBER_COLUMNS
}) => {
    const [ariaBusy, setAriaBusy] = useState(true)
    const [activeLink, setActiveLink] = useState()
    const [activeItemId, setActiveItemId] = useState(null)
    const [isClosing, setIsClosing] = useState(false)
    const [keyboardOpenedId, setKeyboardOpenedId] = useState(null)
    const intl = useIntl()
    const {data: globalNavigationData} = useGlobalNavigation()
    const {data: headerData} = useGlobalHeader()
    const {category, shouldShowHeader, headerVariant} = useHeaderContext()
    const isTransparentHeader = headerVariant === '1' || headerVariant === '2'
    const baseStyle = useMultiStyleConfig('ListMenu', {})
    const items = root?.[itemsKey]
    const getLinkData = useGetLinkData()

    // CMS-driven first-level mutations (Prepend / Append / Replace) for WebDesktop.
    // Only the first seven items are visible in the bar, mirroring the slice below.
    const firstLevelItems = applyCategoryAddReplace(
        items?.slice(0, 7),
        headerData?.CategoryAddReplace,
        'WebDesktop'
    )

    const openTimerRef = useRef(null)
    const closeTimerRef = useRef(null)
    const closeAnimTimerRef = useRef(null)
    const isOpenRef = useRef(false)
    const isClosingRef = useRef(false)
    const skipOpenAnimationRef = useRef(false)
    const toggleButtonRefs = useRef({})

    // Determine the active top-level category ID from the current page category
    // The parentCategoryTree includes the root category at index 0, so we need index 1 for the actual navigation category
    // If parentCategoryTree has only 1 item (the root), or if we're on a top-level category itself, use category.id
    const activeTopLevelCategoryId =
        category?.parentCategoryTree?.length > 1 ? category.parentCategoryTree[1].id : category?.id

    const clearAllTimers = () => {
        clearTimeout(openTimerRef.current)
        clearTimeout(closeTimerRef.current)
        clearTimeout(closeAnimTimerRef.current)
    }

    const doImmediateClose = () => {
        clearAllTimers()
        setActiveItemId(null)
        setIsClosing(false)
        setKeyboardOpenedId(null)
        isOpenRef.current = false
        isClosingRef.current = false
    }

    const doClose = () => {
        clearAllTimers()
        isClosingRef.current = true
        setIsClosing(true)
        setKeyboardOpenedId(null)
        closeAnimTimerRef.current = setTimeout(() => {
            setActiveItemId(null)
            setIsClosing(false)
            isClosingRef.current = false
            isOpenRef.current = false
        }, CLOSE_ANIMATION_MS)
    }

    const handleItemMouseEnter = (id) => {
        clearTimeout(closeTimerRef.current)
        if (isOpenRef.current || isClosingRef.current) {
            // Already open or in close animation — switch immediately with no delay or open animation
            clearAllTimers()
            isClosingRef.current = false
            skipOpenAnimationRef.current = true
            setIsClosing(false)
            isOpenRef.current = true
            setActiveItemId(id)
        } else {
            // Not open — apply open delay, then animate in
            openTimerRef.current = setTimeout(() => {
                isOpenRef.current = true
                skipOpenAnimationRef.current = false
                setActiveItemId(id)
            }, OPEN_DELAY)
        }
    }

    const handleMenuMouseLeave = () => {
        clearTimeout(openTimerRef.current)
        closeTimerRef.current = setTimeout(() => {
            doClose()
        }, CLOSE_DELAY)
    }

    const handleMenuMouseEnter = () => {
        clearTimeout(closeTimerRef.current)
    }

    // Callback ref so each item registers its toggle button element
    const setToggleButtonRef = useCallback((id, el) => {
        toggleButtonRefs.current[id] = el
    }, [])

    // Keyboard toggle: open immediately (no delay), or close if already this item
    const handleKeyboardToggle = (id) => {
        if (activeItemId === id && isOpenRef.current) {
            doImmediateClose()
        } else {
            clearAllTimers()
            isClosingRef.current = false
            skipOpenAnimationRef.current = false
            setIsClosing(false)
            isOpenRef.current = true
            setKeyboardOpenedId(id)
            setActiveItemId(id)
        }
    }

    // Esc: close and return keyboard focus to the toggle button that opened the menu
    const handleEscapeKey = (id) => {
        doImmediateClose()
        toggleButtonRefs.current[id]?.focus()
    }

    // Tab-out: close without moving focus (focus has already moved naturally)
    const handleFocusLeave = () => {
        doImmediateClose()
    }

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    // Close immediately when the header scrolls out of view
    useEffect(() => {
        if (!shouldShowHeader && (isOpenRef.current || isClosingRef.current)) {
            doImmediateClose()
        }
    }, [shouldShowHeader])

    // Close immediately when the window loses or regains focus (prevents ghost hover state)
    useEffect(() => {
        const blockOpen = () => {
            doImmediateClose()
        }
        window.addEventListener('blur', blockOpen)
        window.addEventListener('focus', blockOpen)
        return () => {
            window.removeEventListener('blur', blockOpen)
            window.removeEventListener('focus', blockOpen)
        }
    }, [])

    return (
        <nav
            id="list-menu"
            className="main-nav"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation'
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            <Flex {...baseStyle.container}>
                {items ? (
                    <Stack {...baseStyle.stackContainer}>
                        {firstLevelItems?.map?.((entry) => {
                            // CMS-injected CTA from CategoryAddReplace. When the block is a
                            // GDICtaBlock styled as "Mega Menu Link" (or unstyled), render
                            // as a plain nav link so it inherits the navigation's existing
                            // spacing/hover/animation. Otherwise let the block render with
                            // its own authored styling via the shared GDI renderer.
                            if (entry.__cmsItemType === 'cta') {
                                const megaLinkData = getMegaMenuLinkCtaData(entry.block)
                                if (megaLinkData) {
                                    const linkBlock = megaLinkData.linkBlock
                                    const fallbackCategory = entry.fallbackCategory
                                    const fallbackItemsCount = fallbackCategory
                                        ? fallbackCategory[itemsCountKey] ||
                                          fallbackCategory[itemsKey]?.length ||
                                          0
                                        : 0
                                    // Mega Menu Link styling shared across all branches —
                                    // applied either to the anchor rendered by GDILinkBlock
                                    // (for any usable LinkType) or to the plain fallback link.
                                    const navLinkStyle = {
                                        ...baseStyle.listMenuTriggerLink,
                                        position: 'relative',
                                        sx: getMegaMenuLinkSx(),
                                        ...megaLinkData.styleProps,
                                        _hover: {
                                            textDecoration: 'none',
                                            ...megaLinkData.hoverStyleProps
                                        }
                                    }

                                    // Configured link — delegate all LinkType handling
                                    // (Internal/External/Product/Category/CMS Page/Custom
                                    // Drawer/System Drawer ID) to GDILinkBlock. Styling is
                                    // forwarded via the linkProps passthrough so the nav's
                                    // typography/spacing/underline animation is preserved.
                                    if (isGDILinkUsable(linkBlock, getLinkData)) {
                                        return (
                                            <Box
                                                key={entry.key}
                                                {...baseStyle.listMenuTriggerContainer}
                                            >
                                                <GDILinkBlock
                                                    {...linkBlock}
                                                    AriaLabel={
                                                        megaLinkData.ariaLabel ||
                                                        linkBlock?.AriaLabel
                                                    }
                                                    linkProps={navLinkStyle}
                                                >
                                                    {megaLinkData.text}
                                                </GDILinkBlock>
                                                <Box
                                                    aria-hidden="true"
                                                    flexShrink={0}
                                                    width="8px"
                                                    height="16px"
                                                />
                                            </Box>
                                        )
                                    }

                                    // No usable CMS link. When the referenced category has
                                    // children, render as a popover trigger so users can
                                    // drill into the next level with the CTA's authored
                                    // "Mega Menu Link" text/styling.
                                    if (fallbackItemsCount > 0) {
                                        const ctaPopoverId = fallbackCategory.id
                                        const isThisCtaOpen = activeItemId === ctaPopoverId
                                        return (
                                            <Box
                                                key={entry.key}
                                                onMouseEnter={() =>
                                                    handleItemMouseEnter(ctaPopoverId)
                                                }
                                                onMouseLeave={handleMenuMouseLeave}
                                            >
                                                <ListMenuPopover
                                                    key={ctaPopoverId}
                                                    maxColumns={maxColumns}
                                                    item={fallbackCategory}
                                                    name={megaLinkData.text}
                                                    items={fallbackCategory?.[itemsKey]}
                                                    itemsKey={itemsKey}
                                                    categories={categories}
                                                    contentComponent={contentComponent}
                                                    isActive={
                                                        activeTopLevelCategoryId ===
                                                        ctaPopoverId
                                                    }
                                                    isOpen={isThisCtaOpen}
                                                    isKeyboardOpen={
                                                        isThisCtaOpen &&
                                                        keyboardOpenedId === ctaPopoverId
                                                    }
                                                    isClosing={
                                                        isThisCtaOpen && isClosing
                                                    }
                                                    skipOpenAnimation={
                                                        isThisCtaOpen &&
                                                        skipOpenAnimationRef.current
                                                    }
                                                    onClose={doImmediateClose}
                                                    onMenuMouseEnter={handleMenuMouseEnter}
                                                    onMenuMouseLeave={handleMenuMouseLeave}
                                                    onKeyboardToggle={() =>
                                                        handleKeyboardToggle(ctaPopoverId)
                                                    }
                                                    onEscapeKey={() =>
                                                        handleEscapeKey(ctaPopoverId)
                                                    }
                                                    onFocusLeave={handleFocusLeave}
                                                    toggleButtonRef={(el) =>
                                                        setToggleButtonRef(ctaPopoverId, el)
                                                    }
                                                    triggerDisplayName={megaLinkData.text}
                                                    triggerAriaLabel={
                                                        megaLinkData.ariaLabel || undefined
                                                    }
                                                    triggerLinkProps={megaLinkData.styleProps}
                                                    triggerHoverProps={
                                                        megaLinkData.hoverStyleProps
                                                    }
                                                    triggerSx={getMegaMenuLinkSx()}
                                                />
                                            </Box>
                                        )
                                    }

                                    // No link and no children — navigate to the referenced
                                    // category's URL.
                                    const href = fallbackCategory
                                        ? categoryUrlBuilder(fallbackCategory, categories)
                                        : '#'
                                    return (
                                        <Box
                                            key={entry.key}
                                            {...baseStyle.listMenuTriggerContainer}
                                        >
                                            <Link
                                                as={RouteLink}
                                                to={href}
                                                aria-label={megaLinkData.ariaLabel || undefined}
                                                {...navLinkStyle}
                                            >
                                                {megaLinkData.text}
                                            </Link>
                                            <Box
                                                aria-hidden="true"
                                                flexShrink={0}
                                                width="8px"
                                                height="16px"
                                            />
                                        </Box>
                                    )
                                }
                                // Non "Mega Menu Link" CTA — render via the shared GDI
                                // renderer so the block keeps its authored button styling
                                // (Button Solid, etc.). Same nav-fallback logic as the
                                // MegaMenuLink branch:
                                //   - usable CMS link → GDICtaBlock navigates itself
                                //   - no link + fallback has children → the styled button
                                //     becomes the ListMenuPopover trigger (hover opens
                                //     children popover)
                                //   - no link + no children → synthesize an Internal Link
                                //     to the referenced category so the button navigates
                                const linkBlock =
                                    entry.block?.GDICtaBlockLink?.[0]?.ContentLink?.Expanded ||
                                    null
                                const fallbackCategory = entry.fallbackCategory
                                const fallbackItemsCount = fallbackCategory
                                    ? fallbackCategory[itemsCountKey] ||
                                      fallbackCategory[itemsKey]?.length ||
                                      0
                                    : 0
                                const linkUsable = isGDILinkUsable(linkBlock, getLinkData)

                                if (!linkUsable && fallbackItemsCount > 0) {
                                    const ctaPopoverId = fallbackCategory.id
                                    const isThisCtaOpen = activeItemId === ctaPopoverId
                                    return (
                                        <Box
                                            key={entry.key}
                                            onMouseEnter={() =>
                                                handleItemMouseEnter(ctaPopoverId)
                                            }
                                            onMouseLeave={handleMenuMouseLeave}
                                        >
                                            <ListMenuPopover
                                                maxColumns={maxColumns}
                                                item={fallbackCategory}
                                                name={
                                                    entry.block
                                                        ?.GDICtaBlockStylesPerViewType?.find?.(
                                                            (style) => style?.CtaText
                                                        )?.CtaText || ''
                                                }
                                                items={fallbackCategory?.[itemsKey]}
                                                itemsKey={itemsKey}
                                                categories={categories}
                                                contentComponent={contentComponent}
                                                isActive={
                                                    activeTopLevelCategoryId === ctaPopoverId
                                                }
                                                isOpen={isThisCtaOpen}
                                                isKeyboardOpen={
                                                    isThisCtaOpen &&
                                                    keyboardOpenedId === ctaPopoverId
                                                }
                                                isClosing={
                                                    isThisCtaOpen && isClosing
                                                }
                                                skipOpenAnimation={
                                                    isThisCtaOpen &&
                                                    skipOpenAnimationRef.current
                                                }
                                                onClose={doImmediateClose}
                                                onMenuMouseEnter={handleMenuMouseEnter}
                                                onMenuMouseLeave={handleMenuMouseLeave}
                                                onKeyboardToggle={() =>
                                                    handleKeyboardToggle(ctaPopoverId)
                                                }
                                                onEscapeKey={() =>
                                                    handleEscapeKey(ctaPopoverId)
                                                }
                                                onFocusLeave={handleFocusLeave}
                                                toggleButtonRef={(el) =>
                                                    setToggleButtonRef(ctaPopoverId, el)
                                                }
                                                customTrigger={renderComponentsSwitch(
                                                    entry.block
                                                )}
                                            />
                                        </Box>
                                    )
                                }
                                const blockToRender =
                                    !linkUsable && fallbackCategory
                                        ? {
                                              ...entry.block,
                                              GDICtaBlockLink: [
                                                  {
                                                      ContentLink: {
                                                          Expanded: {
                                                              __typename: 'GDILinkBlock',
                                                              LinkType: GDI_LINK_TYPES.INTERNAL,
                                                              LinkReference: categoryUrlBuilder(
                                                                  fallbackCategory,
                                                                  categories
                                                              )
                                                          }
                                                      }
                                                  }
                                              ]
                                          }
                                        : entry.block
                                return (
                                    <Box key={entry.key} {...baseStyle.listMenuTriggerContainer}>
                                        {renderComponentsSwitch(blockToRender)}
                                    </Box>
                                )
                            }

                            const item = entry.category
                            const processedCategoryWithCMS = mergeCMSCategoryReplacements(
                                item,
                                globalNavigationData
                            )
                            const {id, name} = processedCategoryWithCMS
                            const itemsCount =
                                processedCategoryWithCMS[itemsCountKey] ||
                                processedCategoryWithCMS[itemsKey]?.length ||
                                0
                            const cmsCategoryReplacement =
                                processedCategoryWithCMS.CategoryReplacement?.ContentLink?.Expanded
                            // A category should render its mega menu popover whenever it
                            // has SFCC subcategories OR any CMS-authored mega menu content
                            // (page-level MegaMenu config, legacy CategoryReplacement mega
                            // menu block, or ShopMenuContent illustrations). Without this
                            // check, categories with CMS content but no SFCC children
                            // (e.g. grg_early_access) would silently fall back to a plain
                            // link and the authored content would never render.
                            const hasCmsMegaMenuContent = Boolean(
                                processedCategoryWithCMS.cmsMegaMenuMode ||
                                    processedCategoryWithCMS.hasCMSMegaMenu ||
                                    processedCategoryWithCMS.CMSMegaMenuData ||
                                    processedCategoryWithCMS.MegaMenuIlustrationBlockList?.length
                            )
                            const shouldRenderPopover =
                                itemsCount > 0 || hasCmsMegaMenuContent
                            const isActive = activeTopLevelCategoryId === id
                            const isThisItemOpen = activeItemId === id

                            return (
                                <Box
                                    key={id}
                                    onMouseEnter={
                                        shouldRenderPopover
                                            ? () => handleItemMouseEnter(id)
                                            : undefined
                                    }
                                    onMouseLeave={
                                        shouldRenderPopover ? handleMenuMouseLeave : undefined
                                    }
                                >
                                    {shouldRenderPopover ? (
                                        <ListMenuPopover
                                            key={id}
                                            maxColumns={maxColumns}
                                            item={processedCategoryWithCMS}
                                            name={name}
                                            items={item?.[itemsKey]}
                                            itemsKey={itemsKey}
                                            categories={categories}
                                            contentComponent={contentComponent}
                                            isActive={isActive}
                                            isOpen={isThisItemOpen}
                                            isKeyboardOpen={isThisItemOpen && keyboardOpenedId === id}
                                            isClosing={isThisItemOpen && isClosing}
                                            skipOpenAnimation={
                                                isThisItemOpen && skipOpenAnimationRef.current
                                            }
                                            onClose={doImmediateClose}
                                            onMenuMouseEnter={handleMenuMouseEnter}
                                            onMenuMouseLeave={handleMenuMouseLeave}
                                            onKeyboardToggle={() => handleKeyboardToggle(id)}
                                            onEscapeKey={() => handleEscapeKey(id)}
                                            onFocusLeave={handleFocusLeave}
                                            toggleButtonRef={(el) => setToggleButtonRef(id, el)}
                                        />
                                    ) : (
                                        <Box {...baseStyle.listMenuTriggerContainer}>
                                            <Link
                                                as={RouteLink}
                                                to={categoryUrlBuilder(item, categories)}
                                                onMouseOver={setActiveLink.bind(this, id)}
                                                onMouseOut={setActiveLink.bind(this)}
                                                {...baseStyle.listMenuTriggerLink}
                                                {...{name: name + ' __'}}
                                                {...(activeLink === id || isActive
                                                    ? baseStyle.listMenuTriggerLinkActive
                                                    : {})}
                                                /* CMS Props - suppressed on transparent header */
                                                color={
                                                    isTransparentHeader
                                                        ? undefined
                                                        : cmsCategoryReplacement?.TextColor
                                                }
                                                backgroundColor={
                                                    isTransparentHeader
                                                        ? undefined
                                                        : cmsCategoryReplacement?.TextBackgroundColor
                                                }
                                            >
                                                {cmsCategoryReplacement?.OverrideName || name}
                                            </Link>
                                            {/* Invisible spacer — matches the 8px toggle button on items with submenus */}
                                            <Box
                                                aria-hidden="true"
                                                flexShrink={0}
                                                width="8px"
                                                height="16px"
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )
                        })}
                    </Stack>
                ) : (
                    <Center {...baseStyle.spinnerContainer}>
                        <Spinner size="lg" />
                    </Center>
                )}
            </Flex>

            {/* Single overlay shared across all popovers */}
            {activeItemId !== null && <Box {...baseStyle.popoverOverlay} />}
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {
    root: PropTypes.object,
    /**
     * The maximum number of columns that we want to use per row in the menu.
     */
    maxColumns: PropTypes.number,
    /**
     * Customize the property representing the items.
     */
    itemsKey: PropTypes.string,
    /**
     * Cusomtize the property representing the items count.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Customize the component used to render the list menu item
     */
    contentComponent: PropTypes.elementType,
    /**
     * Map of categoryId -> full category data (includes c_relativeUrl) from SSR
     */
    categories: PropTypes.object
}

export {ListMenu}
export {ListMenuTrigger} from './partial/list-menu-trigger'
export {ListMenuPopover} from './partial/list-menu-popover'
export {ListMenuContent} from './partial/list-menu-content'
